import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listRoles,
  listPermissions,
  createRole,
  deleteRole,
  setRolePermissions,
  type Role,
} from "../api/roles";

export function RolesPage() {
  const qc = useQueryClient();
  const { data: roles, isLoading } = useQuery({ queryKey: ["roles"], queryFn: listRoles });
  const { data: catalog } = useQuery({ queryKey: ["permissions"], queryFn: listPermissions });
  const [selected, setSelected] = useState<number | null>(null);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const add = useMutation({
    mutationFn: () => createRole({ name: newName, description: newDesc || undefined }),
    onSuccess: () => {
      setNewName("");
      setNewDesc("");
      qc.invalidateQueries({ queryKey: ["roles"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteRole(id),
    onSuccess: () => {
      setSelected(null);
      qc.invalidateQueries({ queryKey: ["roles"] });
    },
  });

  if (isLoading) return <p className="text-gray-500">Loading roles…</p>;
  const roleList = roles ?? [];
  const activeRole = roleList.find((r) => r.role_id === selected) ?? null;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Roles &amp; Permissions</h1>
      <p className="text-sm text-gray-500 mb-4">
        A role grants a set of permissions. Granting <code>admin</code> grants everything; otherwise
        a permission like <code>campaign</code> also covers <code>campaign_add</code>,{" "}
        <code>campaign_edit</code>, etc.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Permissions</th>
                  <th className="px-4 py-2 text-right">Users</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {roleList.map((r) => (
                  <tr
                    key={r.role_id}
                    className={`cursor-pointer ${selected === r.role_id ? "bg-brand/5" : "hover:bg-gray-50"}`}
                    onClick={() => setSelected(r.role_id)}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-gray-500">{r.description || "—"}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{r.permissions.length}</td>
                    <td className="px-4 py-2 text-right">{r.user_count}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Delete role "${r.name}"? Users lose this role.`))
                            remove.mutate(r.role_id);
                        }}
                        className="text-red-600 hover:underline text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-4 space-y-2">
            <div className="text-sm font-semibold text-gray-700">New role</div>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Description"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
            <button
              disabled={!newName || add.isPending}
              onClick={() => add.mutate()}
              className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
            >
              Add role
            </button>
          </div>
        </div>

        <div>
          {activeRole ? (
            <PermissionEditor
              key={activeRole.role_id}
              role={activeRole}
              catalog={(catalog ?? []).map((p) => p.name)}
              onSaved={() => qc.invalidateQueries({ queryKey: ["roles"] })}
            />
          ) : (
            <p className="text-gray-400 text-sm">Select a role to edit its permissions.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PermissionEditor({
  role,
  catalog,
  onSaved,
}: {
  role: Role;
  catalog: string[];
  onSaved: () => void;
}) {
  // Union of catalog + this role's current perms (some may not be catalogued yet).
  const known = Array.from(new Set([...catalog, ...role.permissions])).sort();
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));
  const [custom, setCustom] = useState("");

  const save = useMutation({
    mutationFn: () => setRolePermissions(role.role_id, Array.from(selected)),
    onSuccess: onSaved,
  });

  function toggle(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function addCustom() {
    const v = custom.trim();
    if (v) {
      setSelected((prev) => new Set(prev).add(v));
      setCustom("");
    }
  }

  const allNames = Array.from(new Set([...known, ...selected])).sort();

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-sm font-semibold text-gray-700 mb-3">
        Permissions for <span className="text-brand">{role.name}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4 max-h-80 overflow-auto">
        {allNames.map((name) => (
          <label key={name} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={selected.has(name)} onChange={() => toggle(name)} />
            <code className="text-xs">{name}</code>
          </label>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <input
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="Add a permission string (e.g. evaluation)"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCustom()}
        />
        <button onClick={addCustom} className="bg-gray-100 hover:bg-gray-200 rounded px-3 py-2 text-sm">
          Add
        </button>
      </div>
      <button
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
      >
        {save.isPending ? "Saving…" : "Save permissions"}
      </button>
    </div>
  );
}
